/* ============================================================
   shop3dView — cca635b3: pure helpers for the shop 3D product
   viewer (interactive display of the seller-uploaded model).

   The product gallery contract carries only image/video labels
   (ProductMediaKind), but the ME-1 media engine hosts model
   files publicly under /media/models/… — so the model-ness of a
   gallery row is derived from its URL extension, not the label.
   Everything here is pure so tests pin the posture without
   WebGL; MediaGallery/Model3DPreview own the canvas.

   cca635b3 scope note: texture application is NOT implemented —
   no seller texture upload exists yet (color-only v1, the gap is
   documented in docs/shop-3d-viewer.md).
   ============================================================ */

import { MODEL_FORMATS } from './model3dView.ts';

/** URL extensions three.js renders live (maps onto MODEL_FORMATS values). */
const VIEWABLE_MODEL_EXTENSIONS: Readonly<Record<string, number>> = {
  stl: MODEL_FORMATS.Stl,
  obj: MODEL_FORMATS.Obj,
  '3mf': MODEL_FORMATS.ThreeMf,
};

/**
 * Model extension of a media URL, lowercased, or null.
 * Tolerates query strings and fragments (engine URLs may carry cache busters);
 * STEP/STP return their extension too — callers decide viewability.
 */
export function modelUrlExtension(url: string | null | undefined): string | null {
  if (typeof url !== 'string' || url.length === 0) return null;
  let path = url;
  const query = path.indexOf('?');
  if (query >= 0) path = path.slice(0, query);
  const hash = path.indexOf('#');
  if (hash >= 0) path = path.slice(0, hash);
  const dot = path.lastIndexOf('.');
  const slash = path.lastIndexOf('/');
  if (dot <= slash) return null; // no extension (or dot inside a directory name)
  const ext = path.slice(dot + 1).toLowerCase();
  return ext.length >= 2 && ext.length <= 5 ? ext : null;
}

/** True when the URL points at a mesh three.js parses client-side (STL/OBJ/3MF). */
export function isViewableModelUrl(url: string | null | undefined): boolean {
  const ext = modelUrlExtension(url);
  return ext != null && ext in VIEWABLE_MODEL_EXTENSIONS;
}

/**
 * Mesh format value (MODEL_FORMATS) a gallery URL carries, or null.
 * STEP/STP are model files but have no mesh — they stay photo items.
 */
export function modelFormatFromUrl(url: string | null | undefined): number | null {
  const ext = modelUrlExtension(url);
  if (ext == null) return null;
  return VIEWABLE_MODEL_EXTENSIONS[ext] ?? null;
}

export type GalleryItemKind = 'image' | 'video' | 'model';

/**
 * Effective gallery kind of a media row: a viewable-model URL wins over
 * the stored label (the label is image/video-only by API contract, so the
 * extension is the source of truth for display). Non-viewable URLs keep
 * the declared kind untouched.
 */
export function galleryItemKind(
  url: string | null | undefined,
  declaredKind: 'image' | 'video' | string | null | undefined,
): GalleryItemKind {
  if (isViewableModelUrl(url)) return 'model';
  return declaredKind === 'video' ? 'video' : 'image';
}
